// ============================
// File: src/pages/DiscoveryPage.jsx
// ============================
import React, { useEffect, useState } from "react";
import { DiscoveryPlanner } from "../features/discovery";
import DemandPageHeader from "../components/DemandPageHeader";
import DemandSelector from "../components/DemandSelector";
import { useDemandId } from "../context/DemandContext";
import { get } from "../api/http";

export default function DiscoveryPage() {
  // Selection comes from the shared context rather than useParams(), which returned
  // {} whenever this page rendered under Dashboard.js instead of a Route. The
  // context also owns the URL sync, so the manual pushState calls are gone.
  const { demandId: currentDemandId, setDemandId: handleDemandChange } = useDemandId();
  const [loading, setLoading] = useState(!currentDemandId);
  const [error, setError] = useState("");

  // With nothing selected, land on the first demand in Discovery.
  useEffect(() => {
    if (currentDemandId) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError("");
        const result = await get('/demands/list', { query: { limit: 200 } });
        if (cancelled) return;
        const discoveryDemands = (result?.demands || []).filter(d =>
          d.current_stage?.toLowerCase().startsWith('discovery')
        );
        if (discoveryDemands.length > 0) {
          handleDemandChange(discoveryDemands[0].demand_id);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [currentDemandId, handleDemandChange]);

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <DemandPageHeader
          title="Discovery"
          description="AI-powered path recommendation and discovery planning"
          breadcrumbPath="Discovery"
        >
          <DemandSelector currentDemandId={null} stage="discovery" onDemandChange={handleDemandChange} />
        </DemandPageHeader>
        <div className="mx-6 my-8 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900">
          Could not load demands: {error}
        </div>
      </div>
    );
  }

  // Show loading state while fetching first demand
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-solid border-blue-600 border-t-transparent mb-4"></div>
          <p className="text-lg font-medium text-gray-700">Loading Discovery demands...</p>
        </div>
      </div>
    );
  }

  // If no demand found, show elegant empty state
  if (!currentDemandId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <DemandPageHeader
          title="Discovery"
          description="AI-powered path recommendation and discovery planning"
          breadcrumbPath="Discovery"
        >
          <DemandSelector
            currentDemandId={null}
            stage="discovery"
            onDemandChange={handleDemandChange}
          />
        </DemandPageHeader>

        <div className="flex items-center justify-center px-6 py-20">
          <div className="max-w-2xl w-full">
            <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
              {/* Header Section */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-12 text-center">
                <div className="text-7xl mb-4">🔍</div>
                <h2 className="text-3xl font-bold text-white mb-2">
                  No Demands in Discovery
                </h2>
                <p className="text-blue-100 text-lg">
                  Ready to start your discovery journey?
                </p>
              </div>

              {/* Content Section */}
              <div className="px-8 py-10">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-xl">📋</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Use the Demand Selector</h3>
                      <p className="text-gray-600 text-sm">
                        Select any demand from the dropdown above to view or edit it in Discovery mode.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <span className="text-xl">✨</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Create a New Demand</h3>
                      <p className="text-gray-600 text-sm">
                        Start from the Home page to create a new demand and move it through the workflow.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                      <span className="text-xl">🎯</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Discovery Process</h3>
                      <p className="text-gray-600 text-sm">
                        Sketch your business process, define constraints, and get AI-powered implementation recommendations.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Button */}
                <div className="mt-10 text-center">
                  <button
                    onClick={() => window.location.href = '/aieaai'}
                    className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                  >
                    <span>←</span>
                    <span>Back to Home</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <DemandPageHeader
        title="Discovery"
        description="AI-powered path recommendation and discovery planning"
        breadcrumbPath="Discovery"
      >
        <DemandSelector
          currentDemandId={currentDemandId}
          stage="discovery"
          onDemandChange={handleDemandChange}
        />
      </DemandPageHeader>
      <div className="px-6 py-6">
        <DiscoveryPlanner demandId={currentDemandId} />
      </div>
    </div>
  );
}
